import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchUnreceivedShipments } from '../../services/receivingAPI';
import {
  getCachedShipments,
  cacheShipments,
} from '../../services/shipmentCache';
import type { AirtableShipment } from '../../types/airtableTypes';
import { buildShipmentDetailNavState, type ShipmentDetailNavState } from './shipmentDetailNavState';
import './ShipmentsList.css';

interface ShipmentBoxProps {
  shipment: AirtableShipment;
  navState: ShipmentDetailNavState;
}

function AllShipmentsBox({ navState }: { navState: ShipmentDetailNavState }): React.ReactElement {
  return (
    <li>
      <Link
        className="shipment-box-container shipment-box-all"
        to="/receive/shipment/all"
        state={navState}
      >
        <div className="shipment-box-content">
          <span className="shipment-box-name">All Shipments</span>
          <span className="shipment-box-description">Search items across all open shipments</span>
        </div>
      </Link>
    </li>
  );
}

function ShipmentBox({ shipment, navState }: ShipmentBoxProps): React.ReactElement {
  const formatDate = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const etaFormatted = formatDate(shipment.etaSF);

  return (
    <li>
      <Link
        className="shipment-box-container"
        to={`/receive/shipment/${shipment.id}`}
        state={navState}
      >
        <div className="shipment-box-content">
          <span className="shipment-box-name">{shipment.name}</span>
          {shipment.description && (
            <span className="shipment-box-description">{shipment.description}</span>
          )}
          {etaFormatted && (
            <span className="shipment-box-eta">ETA: {etaFormatted}</span>
          )}
        </div>
      </Link>
    </li>
  );
}

function ShipmentsList() {
  const [shipments, setShipments] = useState<AirtableShipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadShipments = async () => {
      // Show cached data immediately for instant display (stale-while-revalidate)
      const cachedShipments = getCachedShipments();
      const hadCache = cachedShipments && cachedShipments.length > 0;
      if (hadCache) {
        setShipments(cachedShipments);
        setLoading(false);
      }
      
      // Always fetch fresh data in the background
      const data = await fetchUnreceivedShipments();
      if (cancelled) return;
      
      // Only update state and cache if the API call was successful (no error)
      if (data.error) {
        console.error('Shipments fetch error:', data.error);
        setError(data.error);
        // If we had cached data but refresh failed, show the refresh failed indicator
        if (hadCache) {
          setRefreshFailed(true);
        }
        // Don't replace cached shipments with empty array on error
        // Don't cache error responses
        setLoading(false);
        return;
      }
      
      const freshShipments = data.shipments || [];
      setShipments(freshShipments);
      cacheShipments(freshShipments);
      setError(null);
      setRefreshFailed(false);
      setLoading(false);
    };

    loadShipments();

    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="shipments-container">
        <p className="shipments-list-status">Loading...</p>
      </div>
    );
  }

  if (error && shipments.length === 0) {
    return (
      <div className="shipments-container">
        <p className="shipments-list-status">Error Loading Shipments</p>
        <p className="shipments-error">{error}</p>
      </div>
    );
  }

  const navState = buildShipmentDetailNavState(shipments);

  return (
    <div className="shipments-container">
      {refreshFailed && (
        <p className="shipments-refresh-failed">Unable to refresh</p>
      )}
      {shipments.length === 0 ? (
        <p className="shipments-list-status">No Pending Shipments</p>
      ) : null}
      <ul>
        <AllShipmentsBox navState={navState} />
        {shipments.map((shipment) => (
          <ShipmentBox
            key={shipment.id}
            shipment={shipment}
            navState={navState}
          />
        ))}
      </ul>
    </div>
  );
}

export default ShipmentsList;
